import { useCallback, useEffect, useRef } from 'react';
import { gtm, viewElement } from 'tracking';

/**
 * Hook to track visibility of a tab content
 */
const useVisibilityTracking = ({
  activeTab,
  contentRef,
  dwellTime = 500,
  index,
  tabTitle,
  threshold = 90,
  trackingRef,
}: {
  activeTab: number | null;
  contentRef: React.RefObject<HTMLDivElement>;
  dwellTime?: number;
  index: number;
  tabTitle: string;
  threshold?: number;
  trackingRef: React.MutableRefObject<boolean>;
}): void => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const calculateVisibility = useCallback((): void => {
    if (contentRef.current) {
      const element = contentRef.current.getBoundingClientRect();
      const totalHeight = contentRef.current.scrollHeight;
      const visibleHeight = Math.min(
        window.innerHeight - element.top,
        element.height
      );
      const percentageSeen = Math.max(
        0,
        Math.min(100, (visibleHeight / totalHeight) * 100)
      );

      if (percentageSeen > threshold && !trackingRef.current) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
          gtm(
            viewElement({
              element: `PDP ${tabTitle}`,
            })
          );
          trackingRef.current = true;
        }, dwellTime);
      }
    }
  }, [contentRef, tabTitle, threshold, trackingRef, dwellTime]);

  useEffect(() => {
    const handleScroll = (): void => {
      if (activeTab !== index) return;
      calculateVisibility();
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [activeTab, index, tabTitle, calculateVisibility]);

  useEffect(() => {
    if (activeTab === index) {
      // Timeout is needed to wait for the tab to be visible
      setTimeout(calculateVisibility, 500);
    } else {
      trackingRef.current = false;
    }
  }, [activeTab, index, trackingRef, calculateVisibility]);
};

export default useVisibilityTracking;
